import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from '../../../../../store';
import { useTranslation } from 'react-i18next';
import { noValidation } from '../../../components/SimpleTextFieldComponent';
import { pushEvent } from '../../../../../utils/clevertapUtils';
import SearchTextField from '../../../components/SearchTextField';
import Grid from '@material-ui/core/Grid/Grid';
import LocationAutoComplete, {
    isLocationValid,
} from '../../../components/autocomplete/LocationAutoComplete';
import Button from '@material-ui/core/Button/Button';
import { useButtonStyles } from '../../../components/styles';
import { signSetAction } from '../../../../../store/signup/actions';
import { useNavigate } from 'react-router-dom';
import { PATH } from '../../../../../routes';
import { LocationSelected } from '../../../../../store/signup/types';
import {
    CLICK_ORG_NAME_NAME_INPUT,
    CLICK_ORG_NAME_CITY_INPUT,
    CLICK_ORG_NAME_SEARCH_BTN,
    VIEW_ORG_NAME_PAGE,
} from 'utils/clevertapEvents';

import { Features } from 'store/featureFlags/types';
import { SuggestionsList } from 'pages/signup/components/SuggestionsList/SuggestionsList';
import { styled } from '@mui/material';
import { normalizeApostrophes } from 'pages/signup/utils/normalizeApostrophes';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';

const GridContainer = styled('div')(({ theme }) => ({
    '& .MuiButtonBase-root': {
        marginTop: 24,
    },
    '& .left': {
        paddingRight: theme.spacing(2),
    },
    '& .right': {
        paddingLeft: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
        '& .left': {
            paddingRight: 0,
            marginBottom: theme.spacing(2),
        },
        '& .right': {
            paddingLeft: 0,
        },
    },
}));

const CenteredTextGrid = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'maxWidth',
})<{ maxWidth: number }>(({ maxWidth }) => ({
    maxWidth,
    textAlign: 'center',
    margin: '0 auto',
}));

const OrganizationAutoComplete: React.FC = () => {
    const push = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);
    const { submit, mainButton } = useButtonStyles();
    const { defaultValueOrg, defaultValueLoc, newSearchUIEnabled, v2SearchAPIEnabled } =
        useSelector((state: AppState) => ({
            defaultValueOrg: state.Signup.organizationName,
            defaultValueLoc: state.Signup.organizationLocation,
            newSearchUIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }));
    const { placeholder, nextLabelText } = useMemo(
        () => ({
            placeholder: t('signup.organizationName.placeholders.search'),
            nextLabelText: t('labels.search'),
        }),
        [t],
    );
    const [organizationName, setOrganizationName] =
        useState<string>(defaultValueOrg);
    const [location, setLocation] = useState<LocationSelected>(defaultValueLoc);
    const onChangeName = (value: string) => {
        setOrganizationName(normalizeApostrophes(value));
    };
    const onClick = () => {
        pushEvent(CLICK_ORG_NAME_SEARCH_BTN, { organizationName });
        dispatch(
            signSetAction({ organizationName, organizationLocation: location }),
        );
        if (newSearchUIEnabled) {
            setSuggestionsOpen(true);
        } else {
            push(PATH.SIGNUP.STEPS.SEARCH_ORGANIZATION);
        }
    };

    useEffect(() => {
        pushEvent(VIEW_ORG_NAME_PAGE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: 'off',
        });
    }, [v2SearchAPIEnabled]);

    return (
        <>
            <GridContainer>
                <CenteredTextGrid container maxWidth={700}>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={6}
                        xl={6}
                        className="left"
                    >
                        <SearchTextField
                            value={organizationName}
                            isFocus={true}
                            type="text"
                            placeholder={placeholder}
                            onChange={onChangeName}
                            onClick={() => pushEvent(CLICK_ORG_NAME_NAME_INPUT)}
                            validation={noValidation}
                            maxLength={255}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={6}
                        xl={6}
                        className="right"
                    >
                        <LocationAutoComplete
                            location={location}
                            onSubmit={setLocation}
                            onClick={() => pushEvent(CLICK_ORG_NAME_CITY_INPUT)}
                        />
                    </Grid>
                </CenteredTextGrid>
                <Button
                    className={`${submit} ${mainButton}`}
                    variant="contained"
                    onClick={onClick}
                    disabled={
                        organizationName.length < 3 ||
                        !isLocationValid(location)
                    }
                >
                    {nextLabelText}
                </Button>
            </GridContainer>
            {newSearchUIEnabled && (
                <SuggestionsList
                    open={suggestionsOpen}
                    reOpen={() => setSuggestionsOpen(true)}
                    onClose={() => setSuggestionsOpen(false)}
                />
            )}
        </>
    );
};

export default React.memo(OrganizationAutoComplete);
